<template>
    <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center min-h-[80vh]">
            <div class="w-12 h-12 border-4 border-primary/10 border-t-primary rounded-full animate-spin"></div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="flex justify-center items-center min-h-[80vh]">
            <BaseCard padding="lg" class="max-w-md text-center">
                <div class="w-16 h-16 mx-auto mb-4 text-danger">
                    <BaseIcon name="exclamation-circle" size="64" color="#dc3545" />
                </div>
                <p class="text-gray-600">{{ error }}</p>
            </BaseCard>
        </div>

        <!-- Stats Content -->
        <div v-else-if="stats" class="max-w-[1400px] mx-auto">
            <!-- Header -->
            <div class="mb-10">
                <div class="flex justify-between items-center flex-wrap gap-6">
                    <h1 class="text-3xl font-bold text-primary tracking-tight">Tableau de bord</h1>

                    <div class="flex items-center gap-3 flex-wrap">
                        <!-- Current Period Badge -->
                        <div class="inline-flex items-center gap-2 px-5 py-3 bg-white rounded-xl shadow-md text-sm font-medium text-primary">
                            <BaseIcon name="calendar" size="18" />
                            <span>{{ formatDate(stats.period.from) }} - {{ formatDate(stats.period.to) }}</span>
                        </div>

                        <!-- Previous Period Badge -->
                        <div class="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl text-sm font-medium text-gray-600">
                            <span>vs {{ formatDate(stats.previous_period.from) }} - {{ formatDate(stats.previous_period.to) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main Metrics Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <!-- Revenue Card -->
                <BaseCard padding="lg" class="border border-primary/10 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden group">
                    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary-light opacity-0 group-hover:opacity-100 transition-opacity"></div>

                    <div class="flex items-center gap-3 mb-4">
                        <div class="w-11 h-11 bg-gradient-to-br from-primary to-primary-light rounded-xl flex items-center justify-center flex-shrink-0">
                            <BaseIcon name="cash" size="22" color="white" />
                        </div>
                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Chiffre d'affaires</span>
                    </div>

                    <div class="text-3xl font-extrabold text-primary mb-3 tracking-tight">
                        {{ formatCurrency(stats.accounting.ca.current) }}
                    </div>

                    <div class="flex items-center justify-between gap-3 flex-wrap">
                        <BaseBadge :variant="getEvolutionVariant(stats.accounting.ca.evolution)" class="flex items-center gap-1.5">
                            <BaseIcon
                                v-if="stats.accounting.ca.evolution > 0"
                                name="arrow-up"
                                size="14"
                            />
                            <BaseIcon
                                v-else-if="stats.accounting.ca.evolution < 0"
                                name="arrow-down"
                                size="14"
                            />
                            <span>{{ formatEvolution(stats.accounting.ca.evolution) }}</span>
                        </BaseBadge>
                        <span class="text-sm text-gray-400 font-medium">{{ formatCurrency(stats.accounting.ca.evolution_absolute) }}</span>
                    </div>
                </BaseCard>

                <!-- Sales Card -->
                <BaseCard padding="lg" class="border border-primary/10 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden group">
                    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-success to-success-light opacity-0 group-hover:opacity-100 transition-opacity"></div>

                    <div class="flex items-center gap-3 mb-4">
                        <div class="w-11 h-11 bg-gradient-to-br from-success to-success-light rounded-xl flex items-center justify-center flex-shrink-0">
                            <BaseIcon name="shopping-cart" size="22" color="white" />
                        </div>
                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ventes totales</span>
                    </div>

                    <div class="text-3xl font-extrabold text-primary mb-3 tracking-tight">
                        {{ stats.sales.total_sales.current }}
                    </div>

                    <div class="flex items-center justify-between gap-3 flex-wrap">
                        <BaseBadge :variant="getEvolutionVariant(stats.sales.total_sales.evolution)" class="flex items-center gap-1.5">
                            <BaseIcon
                                v-if="stats.sales.total_sales.evolution > 0"
                                name="arrow-up"
                                size="14"
                            />
                            <BaseIcon
                                v-else-if="stats.sales.total_sales.evolution < 0"
                                name="arrow-down"
                                size="14"
                            />
                            <span>{{ formatEvolution(stats.sales.total_sales.evolution) }}</span>
                        </BaseBadge>
                        <span class="text-sm text-gray-400 font-medium">+{{ stats.sales.total_sales.evolution_absolute }} ventes</span>
                    </div>
                </BaseCard>

                <!-- Payments Card -->
                <BaseCard padding="lg" class="border border-primary/10 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden group">
                    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-warning to-warning-light opacity-0 group-hover:opacity-100 transition-opacity"></div>

                    <div class="flex items-center gap-3 mb-4">
                        <div class="w-11 h-11 bg-gradient-to-br from-warning to-warning-light rounded-xl flex items-center justify-center flex-shrink-0">
                            <BaseIcon name="credit-card" size="22" color="white" />
                        </div>
                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paiements reçus</span>
                    </div>

                    <div class="text-3xl font-extrabold text-primary mb-3 tracking-tight">
                        {{ formatCurrency(stats.accounting.total_payments.current) }}
                    </div>

                    <div class="flex items-center justify-between gap-3 flex-wrap">
                        <BaseBadge :variant="getEvolutionVariant(stats.accounting.total_payments.evolution)" class="flex items-center gap-1.5">
                            <BaseIcon
                                v-if="stats.accounting.total_payments.evolution > 0"
                                name="arrow-up"
                                size="14"
                            />
                            <BaseIcon
                                v-else-if="stats.accounting.total_payments.evolution < 0"
                                name="arrow-down"
                                size="14"
                            />
                            <span>{{ formatEvolution(stats.accounting.total_payments.evolution) }}</span>
                        </BaseBadge>
                        <span class="text-sm text-gray-400 font-medium">{{ formatCurrency(stats.accounting.total_payments.evolution_absolute) }}</span>
                    </div>
                </BaseCard>

                <!-- Average Ticket Card -->
                <BaseCard padding="lg" class="border border-primary/10 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden group">
                    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                    <div class="flex items-center gap-3 mb-4">
                        <div class="w-11 h-11 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center flex-shrink-0">
                            <BaseIcon name="cash" size="22" color="white" />
                        </div>
                        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ticket moyen</span>
                    </div>

                    <div class="text-3xl font-extrabold text-primary mb-3 tracking-tight">
                        {{ formatCurrency(stats.sales.average_ticket.current) }}
                    </div>

                    <div class="flex items-center justify-between gap-3 flex-wrap">
                        <BaseBadge :variant="getEvolutionVariant(stats.sales.average_ticket.evolution)" class="flex items-center gap-1.5">
                            <BaseIcon
                                v-if="stats.sales.average_ticket.evolution > 0"
                                name="arrow-up"
                                size="14"
                            />
                            <BaseIcon
                                v-else-if="stats.sales.average_ticket.evolution < 0"
                                name="arrow-down"
                                size="14"
                            />
                            <span>{{ formatEvolution(stats.sales.average_ticket.evolution) }}</span>
                        </BaseBadge>
                        <span class="text-sm text-gray-400 font-medium">{{ formatCurrency(stats.sales.average_ticket.evolution_absolute) }}</span>
                    </div>
                </BaseCard>
            </div>

            <!-- Daily Performance Section -->
            <div class="mb-10">
                <h2 class="text-xl font-bold text-primary mb-5 tracking-tight">Performance journalière</h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Revenue per Day -->
                    <BaseCard padding="lg" class="border border-primary/10 hover:shadow-lg transition-shadow">
                        <div class="mb-3">
                            <span class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Revenus / jour</span>
                        </div>
                        <div class="text-3xl font-extrabold text-primary mb-4 tracking-tight">
                            {{ formatCurrency(stats.evolution.revenue_per_day.current) }}
                        </div>
                        <div class="flex items-center justify-between">
                            <span class="text-sm text-gray-400 font-semibold">{{ formatCurrency(stats.evolution.revenue_per_day.previous) }}</span>
                            <BaseBadge :variant="getEvolutionVariant(stats.evolution.revenue_per_day.evolution)" class="flex items-center gap-1.5">
                                <BaseIcon
                                    v-if="stats.evolution.revenue_per_day.evolution > 0"
                                    name="arrow-up"
                                    size="12"
                                />
                                <BaseIcon
                                    v-else-if="stats.evolution.revenue_per_day.evolution < 0"
                                    name="arrow-down"
                                    size="12"
                                />
                                <span>{{ formatEvolution(stats.evolution.revenue_per_day.evolution) }}</span>
                            </BaseBadge>
                        </div>
                    </BaseCard>

                    <!-- Sales per Day -->
                    <BaseCard padding="lg" class="border border-primary/10 hover:shadow-lg transition-shadow">
                        <div class="mb-3">
                            <span class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Ventes / jour</span>
                        </div>
                        <div class="text-3xl font-extrabold text-primary mb-4 tracking-tight">
                            {{ stats.evolution.sales_per_day.current.toFixed(2) }}
                        </div>
                        <div class="flex items-center justify-between">
                            <span class="text-sm text-gray-400 font-semibold">{{ stats.evolution.sales_per_day.previous.toFixed(2) }}</span>
                            <BaseBadge :variant="getEvolutionVariant(stats.evolution.sales_per_day.evolution)" class="flex items-center gap-1.5">
                                <BaseIcon
                                    v-if="stats.evolution.sales_per_day.evolution > 0"
                                    name="arrow-up"
                                    size="12"
                                />
                                <BaseIcon
                                    v-else-if="stats.evolution.sales_per_day.evolution < 0"
                                    name="arrow-down"
                                    size="12"
                                />
                                <span>{{ formatEvolution(stats.evolution.sales_per_day.evolution) }}</span>
                            </BaseBadge>
                        </div>
                    </BaseCard>
                </div>
            </div>

            <!-- Detailed Stats Grid -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Sales Section -->
                <BaseCard padding="lg" class="border border-primary/10">
                    <div class="flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-100 flex-wrap gap-3">
                        <div class="flex items-center gap-3">
                            <h2 class="text-lg font-bold text-primary uppercase tracking-wide">Ventes</h2>
                            <BaseBadge variant="primary" class="font-bold shadow-md">
                                {{ stats.sales.total_sales.current }}
                            </BaseBadge>
                        </div>
                        <BaseBadge :variant="getEvolutionVariant(stats.sales.total_sales.evolution)" class="flex items-center gap-1.5">
                            <BaseIcon
                                v-if="stats.sales.total_sales.evolution > 0"
                                name="arrow-up"
                                size="12"
                            />
                            <BaseIcon
                                v-else-if="stats.sales.total_sales.evolution < 0"
                                name="arrow-down"
                                size="12"
                            />
                            <span>{{ formatEvolution(stats.sales.total_sales.evolution) }}</span>
                        </BaseBadge>
                    </div>

                    <div class="space-y-3">
                        <!-- Completed Sales -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-success rounded-full shadow-[0_0_0_3px_rgba(46,125,50,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">Complétées</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-base font-bold text-primary">{{ stats.sales.completed_sales.current }}</span>
                                <span class="text-xs font-bold px-2 py-1 rounded-md" :class="{
                                    'bg-success/10 text-success': stats.sales.completed_sales.evolution > 0,
                                    'bg-danger/10 text-danger': stats.sales.completed_sales.evolution < 0,
                                    'bg-gray-100 text-gray-600': stats.sales.completed_sales.evolution === 0
                                }">
                                    {{ formatEvolution(stats.sales.completed_sales.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Pending Sales -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-warning rounded-full shadow-[0_0_0_3px_rgba(237,108,2,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">En attente</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-base font-bold text-primary">{{ stats.sales.pending_sales.current }}</span>
                                <span class="text-xs font-bold px-2 py-1 rounded-md" :class="{
                                    'bg-success/10 text-success': stats.sales.pending_sales.evolution > 0,
                                    'bg-danger/10 text-danger': stats.sales.pending_sales.evolution < 0,
                                    'bg-gray-100 text-gray-600': stats.sales.pending_sales.evolution === 0
                                }">
                                    {{ formatEvolution(stats.sales.pending_sales.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Refunded Sales -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-gray-600 rounded-full shadow-[0_0_0_3px_rgba(102,102,102,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">Remboursées</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-base font-bold text-primary">{{ stats.sales.refunded_sales.current }}</span>
                                <span class="text-xs font-bold px-2 py-1 rounded-md" :class="{
                                    'bg-success/10 text-success': stats.sales.refunded_sales.evolution > 0,
                                    'bg-danger/10 text-danger': stats.sales.refunded_sales.evolution < 0,
                                    'bg-gray-100 text-gray-600': stats.sales.refunded_sales.evolution === 0
                                }">
                                    {{ formatEvolution(stats.sales.refunded_sales.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Divider -->
                        <div class="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-2"></div>

                        <!-- Total Revenue -->
                        <div class="flex justify-between items-center p-5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-primary/15">
                            <span class="text-base text-primary font-bold">Revenus totaux</span>
                            <div class="flex items-center gap-3 flex-wrap">
                                <span class="text-xl font-extrabold text-primary">{{ formatCurrency(stats.sales.total_revenue.current) }}</span>
                                <span class="text-xs font-bold px-2.5 py-1.5 rounded-md" :class="{
                                    'bg-success/20 text-success': stats.sales.total_revenue.evolution > 0,
                                    'bg-danger/20 text-danger': stats.sales.total_revenue.evolution < 0,
                                    'bg-gray-200 text-gray-600': stats.sales.total_revenue.evolution === 0
                                }">
                                    {{ formatEvolution(stats.sales.total_revenue.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Discounts -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <span class="text-sm text-gray-600 font-medium">Remises appliquées</span>
                            <span class="text-base font-bold text-danger">{{ formatCurrency(stats.sales.total_discount.current) }}</span>
                        </div>
                    </div>
                </BaseCard>

                <!-- Invoicing Section -->
                <BaseCard padding="lg" class="border border-primary/10">
                    <div class="flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-100 flex-wrap gap-3">
                        <div class="flex items-center gap-3">
                            <h2 class="text-lg font-bold text-primary uppercase tracking-wide">Facturation</h2>
                            <BaseBadge variant="primary" class="font-bold shadow-md">
                                {{ stats.invoicing.total_invoices.current }}
                            </BaseBadge>
                        </div>
                        <BaseBadge :variant="getEvolutionVariant(stats.invoicing.total_invoices.evolution)" class="flex items-center gap-1.5">
                            <BaseIcon
                                v-if="stats.invoicing.total_invoices.evolution > 0"
                                name="arrow-up"
                                size="12"
                            />
                            <BaseIcon
                                v-else-if="stats.invoicing.total_invoices.evolution < 0"
                                name="arrow-down"
                                size="12"
                            />
                            <span>{{ formatEvolution(stats.invoicing.total_invoices.evolution) }}</span>
                        </BaseBadge>
                    </div>

                    <div class="space-y-3">
                        <!-- Paid Invoices -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-success rounded-full shadow-[0_0_0_3px_rgba(46,125,50,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">Factures payées</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-base font-bold text-primary">{{ stats.invoicing.paid_invoices.current }}</span>
                                <span class="text-xs font-bold px-2 py-1 rounded-md" :class="{
                                    'bg-success/10 text-success': stats.invoicing.paid_invoices.evolution > 0,
                                    'bg-danger/10 text-danger': stats.invoicing.paid_invoices.evolution < 0,
                                    'bg-gray-100 text-gray-600': stats.invoicing.paid_invoices.evolution === 0
                                }">
                                    {{ formatEvolution(stats.invoicing.paid_invoices.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Unpaid Invoices -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-danger rounded-full shadow-[0_0_0_3px_rgba(211,47,47,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">Factures impayées</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-base font-bold text-primary">{{ stats.invoicing.unpaid_invoices.current }}</span>
                                <span class="text-xs font-bold px-2 py-1 rounded-md" :class="{
                                    'bg-success/10 text-success': stats.invoicing.unpaid_invoices.evolution > 0,
                                    'bg-danger/10 text-danger': stats.invoicing.unpaid_invoices.evolution < 0,
                                    'bg-gray-100 text-gray-600': stats.invoicing.unpaid_invoices.evolution === 0
                                }">
                                    {{ formatEvolution(stats.invoicing.unpaid_invoices.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Divider -->
                        <div class="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-2"></div>

                        <!-- Total Amount -->
                        <div class="flex justify-between items-center p-5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-primary/15">
                            <span class="text-base text-primary font-bold">Montant total</span>
                            <div class="flex items-center gap-3 flex-wrap">
                                <span class="text-xl font-extrabold text-primary">{{ formatCurrency(stats.invoicing.total_invoice_amount.current) }}</span>
                                <span class="text-xs font-bold px-2.5 py-1.5 rounded-md" :class="{
                                    'bg-success/20 text-success': stats.invoicing.total_invoice_amount.evolution > 0,
                                    'bg-danger/20 text-danger': stats.invoicing.total_invoice_amount.evolution < 0,
                                    'bg-gray-200 text-gray-600': stats.invoicing.total_invoice_amount.evolution === 0
                                }">
                                    {{ formatEvolution(stats.invoicing.total_invoice_amount.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Average Amount -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <span class="text-sm text-gray-600 font-medium">Montant moyen</span>
                            <div class="flex items-center gap-3">
                                <span class="text-base font-bold text-primary">{{ formatCurrency(stats.invoicing.average_invoice_amount.current) }}</span>
                                <span class="text-xs font-bold px-2 py-1 rounded-md" :class="{
                                    'bg-success/10 text-success': stats.invoicing.average_invoice_amount.evolution > 0,
                                    'bg-danger/10 text-danger': stats.invoicing.average_invoice_amount.evolution < 0,
                                    'bg-gray-100 text-gray-600': stats.invoicing.average_invoice_amount.evolution === 0
                                }">
                                    {{ formatEvolution(stats.invoicing.average_invoice_amount.evolution) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </BaseCard>

                <!-- Products Section -->
                <BaseCard padding="lg" class="border border-primary/10">
                    <div class="flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-100 flex-wrap gap-3">
                        <div class="flex items-center gap-3">
                            <h2 class="text-lg font-bold text-primary uppercase tracking-wide">Produits</h2>
                            <BaseBadge variant="primary" class="font-bold shadow-md">
                                {{ stats.products.total_products }}
                            </BaseBadge>
                        </div>
                    </div>

                    <div class="space-y-3">
                        <!-- Active Products -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-success rounded-full shadow-[0_0_0_3px_rgba(46,125,50,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">Produits actifs</span>
                            </div>
                            <span class="text-base font-bold text-primary">{{ stats.products.active_products }}</span>
                        </div>

                        <!-- In Stock -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-primary rounded-full shadow-[0_0_0_3px_rgba(10,92,107,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">En stock</span>
                            </div>
                            <span class="text-base font-bold text-primary">{{ stats.products.products_with_stock }}</span>
                        </div>

                        <!-- Divider -->
                        <div class="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-2"></div>

                        <!-- Stock Value -->
                        <div class="flex justify-between items-center p-5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-primary/15">
                            <span class="text-base text-primary font-bold">Valeur du stock</span>
                            <span class="text-xl font-extrabold text-primary">{{ formatCurrency(stats.products.total_stock_value) }}</span>
                        </div>
                    </div>
                </BaseCard>

                <!-- Customers Section -->
                <BaseCard padding="lg" class="border border-primary/10">
                    <div class="flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-100 flex-wrap gap-3">
                        <div class="flex items-center gap-3">
                            <h2 class="text-lg font-bold text-primary uppercase tracking-wide">Clients</h2>
                            <BaseBadge variant="primary" class="font-bold shadow-md">
                                {{ stats.customers.total_customers.current }}
                            </BaseBadge>
                        </div>
                        <BaseBadge :variant="getEvolutionVariant(stats.customers.active_customers.evolution)" class="flex items-center gap-1.5">
                            <BaseIcon
                                v-if="stats.customers.active_customers.evolution > 0"
                                name="arrow-up"
                                size="12"
                            />
                            <BaseIcon
                                v-else-if="stats.customers.active_customers.evolution < 0"
                                name="arrow-down"
                                size="12"
                            />
                            <span>{{ formatEvolution(stats.customers.active_customers.evolution) }}</span>
                        </BaseBadge>
                    </div>

                    <div class="space-y-3">
                        <!-- Active Customers -->
                        <div class="flex justify-between items-center p-3.5 rounded-lg hover:bg-gray-50 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-2.5 h-2.5 bg-success rounded-full shadow-[0_0_0_3px_rgba(46,125,50,0.15)]"></div>
                                <span class="text-sm text-gray-600 font-medium">Clients actifs</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-base font-bold text-primary">{{ stats.customers.active_customers.current }}</span>
                                <span class="text-xs font-bold px-2 py-1 rounded-md" :class="{
                                    'bg-success/10 text-success': stats.customers.active_customers.evolution > 0,
                                    'bg-danger/10 text-danger': stats.customers.active_customers.evolution < 0,
                                    'bg-gray-100 text-gray-600': stats.customers.active_customers.evolution === 0
                                }">
                                    {{ formatEvolution(stats.customers.active_customers.evolution) }}
                                </span>
                            </div>
                        </div>

                        <!-- Divider -->
                        <div class="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-2"></div>

                        <!-- Customer Revenue -->
                        <div class="flex justify-between items-center p-5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-primary/15">
                            <span class="text-base text-primary font-bold">Revenus clients</span>
                            <div class="flex items-center gap-3 flex-wrap">
                                <span class="text-xl font-extrabold text-primary">{{ formatCurrency(stats.customers.total_revenue.current) }}</span>
                                <span class="text-xs font-bold px-2.5 py-1.5 rounded-md" :class="{
                                    'bg-success/20 text-success': stats.customers.total_revenue.evolution > 0,
                                    'bg-danger/20 text-danger': stats.customers.total_revenue.evolution < 0,
                                    'bg-gray-200 text-gray-600': stats.customers.total_revenue.evolution === 0
                                }">
                                    {{ formatEvolution(stats.customers.total_revenue.evolution) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </BaseCard>
            </div>
        </div>
    </div>
</template>

<script>
import DashboardService from '@/services/dashboardService'

export default {
    name: 'DashboardStats',
    data() {
        return {
            stats: null,
            loading: false,
            error: null
        }
    },
    mounted() {
        this.fetchDashboardStats()
    },
    methods: {
        async fetchDashboardStats(params = {}) {
            this.loading = true
            this.error = null

            try {
                const response = await DashboardService.getDashboardStats(params)

                if (response.data.status === 'success') {
                    this.stats = response.data.data
                } else {
                    this.error = 'Erreur lors du chargement des statistiques'
                }
            } catch (err) {
                console.error('Error fetching dashboard stats:', err)
                this.error = err.response?.data?.message || 'Une erreur est survenue'
            } finally {
                this.loading = false
            }
        },

        formatCurrency(value) {
            if (!value && value !== 0) return '0 FCFA'
            return new Intl.NumberFormat('fr-FR', {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value) + ' FCFA'
        },

        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        },

        formatEvolution(value) {
            if (value === 0) return '0%'
            const sign = value > 0 ? '+' : ''
            return `${sign}${value.toFixed(1)}%`
        },

        getEvolutionClass(value) {
            if (value > 0) return 'positive'
            if (value < 0) return 'negative'
            return 'neutral'
        },

        getEvolutionVariant(value) {
            if (value > 0) return 'success'
            if (value < 0) return 'danger'
            return 'secondary'
        }
    }
}
</script>

<style scoped>
/* Minimal scoped styles - most styling is done with Tailwind */
</style>
